import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Stack,
    Textarea,
    Tooltip,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { agentsService } from "services/agentsService";
import { personsService } from "services/personsService";
import { checkErrors, hasError } from "utils/forms-utils";
import PersonModal from "../Person/PersonModal";

function AgentNew() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const dialogRef = useRef(undefined);
    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const [agentPersons, setAgentPersons] = useState(undefined);
    const [noteToRemoveIndex, setNoteToRemoveIndex] = useState(null);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});
    const { isOpen: isOpenPerson, onOpen: onOpenPerson, onClose: onClosePerson } = useDisclosure();

    const ids = useMemo(() => ({
        agentPerson: "person",
        agentRegistration: "registration",
        agentObservations: "observations"
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const isPersonValid = !!selectedPerson;
    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => ({
        [ids.agentPerson]: {
            value: data?.id_pessoa || "",
            validations: [{ required: true }],
        },
        [ids.agentRegistration]: {
            value: data?.codigo || "",
            validations: [{ required: true }],
        },
        [ids.agentObservations]: {
            value: data?.observacoes || [],
            validations: [
                {
                    callback: ({ value }) => {
                        if (Array.isArray(value) && value.length > 0) {
                            const hasEmpty = value.some(item => !item || item.trim() === "");
                            if (hasEmpty) return "teste";
                        }
                        return null;
                    },
                },
            ],
        },
    });

    const fetchData = async () => {
        try {
            const res = await personsService.findMany();
            const agentPersons = res?._embedded?.person_list || [];
            setAgentPersons(agentPersons);

            if (id) {
                const agentDetails = await agentsService.find(id);
                setSelectedPerson(agentDetails.id_pessoa);
                setFormData(initFormData(agentDetails));
            } else {
                setFormData(initFormData());
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const toDTO = () => ({
        id_pessoa: formData[ids.agentPerson].value,
        codigo: formData[ids.agentRegistration].value,
        observacoes: formData[ids.agentObservations].value || [],
    });

    const handleSubmit = async () => {
        if (isFormInvalid) return;

        try {
            const data = toDTO();

            if (id) {
                await agentsService.update(id, data);
            } else {
                await agentsService.create(data);
            }

            showToast({
                title: t(`settings.agent.messages.success.${id ? 'edit' : 'create'}`),
                status: "success",
            });

            navigate(ROUTES.agentsPath);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const handleSavePerson = async (personData) => {
        try {
            let updatedPerson;

            if (isPersonValid) {
                const res = await personsService.update(selectedPerson.id, personData);
                updatedPerson = { ...selectedPerson, ...res };
            } else {
                updatedPerson = await personsService.create(personData);
            }

            setAgentPersons((prev) =>
                prev.some((p) => p.id === updatedPerson.id)
                    ? prev.map((p) => (p.id === updatedPerson.id ? updatedPerson : p))
                    : [...prev, updatedPerson]
            );

            setSelectedPerson(updatedPerson);

            setFormData((prev) => {
                const newFormData = {
                    ...prev,
                    [ids.agentPerson]: { ...prev[ids.agentPerson], value: updatedPerson },
                };
                setInputError(checkErrors({ formData: newFormData, inputError, name: ids.agentPerson }));
                return newFormData;
            });

            onClosePerson();

            showToast({
                title: t(`settings.person.messages.success.${isPersonValid ? 'edit' : 'create'}`),
                status: "success",
            });
        } catch (error) {
            showToast({ title: t(error), status: "error" });
            throw error;
        }
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const newFormData = { ...formData, [id]: { ...formData[id], value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    const handlePersonChange = (e) => {
        const id = e.target.value;
        const selected = agentPersons.find(p => p.id === parseInt(id));

        setSelectedPerson(selected);
        const newFormData = {
            ...formData,
            [ids.agentPerson]: {
                ...formData[ids.agentPerson],
                value: selected,
            },
        };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: ids.agentPerson }));
    };

    const handleNoteChange = (index, newValue) => {
        const updatedNotes = [...formData[ids.agentObservations].value];
        updatedNotes[index] = newValue;
        setFormData(prev => ({
            ...prev,
            [ids.agentObservations]: {
                ...prev[ids.agentObservations],
                value: updatedNotes,
            },
        }));
    };

    const handleAddNote = () => {
        const updatedNotes = [...formData[ids.agentObservations].value, ""];
        setFormData(prev => ({
            ...prev,
            [ids.agentObservations]: {
                ...prev[ids.agentObservations],
                value: updatedNotes,
            },
        }));
    };

    const handleRemoveNote = (indexToRemove) => {
        setNoteToRemoveIndex(indexToRemove);
        dialogRef.current?.displayDialog({
            title: t("settings.agent.new.message-delete-note"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const confirmRemoveNote = () => {
        if (noteToRemoveIndex !== null) {
            const updatedNotes = formData[ids.agentObservations].value.filter((_, idx) => idx !== noteToRemoveIndex);
            setFormData(prev => ({
                ...prev,
                [ids.agentObservations]: {
                    ...prev[ids.agentObservations],
                    value: updatedNotes,
                },
            }));
            setNoteToRemoveIndex(null);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "90px" }}>
            <Card>
                <ListPageCardHeader
                    title={t(id ? 'settings.agent.edit.title' : 'settings.agent.new.title')}
                    subtitle={t(id ? 'settings.agent.edit.subtitle' : 'settings.agent.new.subtitle')}
                />
                <CardBody px="22px">
                    {formData && agentPersons ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.agentPerson]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("settings.agent.new.person")}
                                    </FormLabel>
                                    <HStack align="center" justify="space-between">
                                        <SearchSelect
                                            required
                                            id={ids.agentPerson}
                                            value={formData[ids.agentPerson].value?.id || ""}
                                            isDisabled={!agentPersons.length}
                                            items={agentPersons}
                                            valueField="id"
                                            displayField="nome"
                                            onChange={handlePersonChange}
                                            placeholder={t('settings.user.new.placeholder.person')}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.agentPerson])}
                                        </FormErrorMessage>
                                        <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={onOpenPerson}
                                        >
                                            {isPersonValid ? <EditIcon /> : <AddIcon />}
                                        </Button>
                                    </HStack>
                                </FormControl>

                                <FormControl isRequired isInvalid={inputError[ids.agentRegistration]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("settings.agent.new.registration")}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        id={ids.agentRegistration}
                                        value={formData[ids.agentRegistration].value}
                                        onChange={handleInputChange}
                                        maxLength={"250"}
                                        placeholder={t('settings.agent.new.registration-placeholder')}
                                        bg={bgTextarea}
                                        _placeholder={{ color: placeholderColor }}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.agentRegistration])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.agentObservations]}>
                                    <HStack spacing={cardSpacing} mb={2}>
                                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs" mt={4}>
                                            {t("settings.agent.new.predefined-notes")}
                                        </FormLabel>
                                        <Button
                                            leftIcon={<AddIcon />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={handleAddNote}
                                        >
                                            {t("settings.agent.new.add-new-note")}
                                        </Button>
                                    </HStack>

                                    <Stack spacing={3}>
                                        {formData[ids.agentObservations].value.map((note, index) => (
                                            <HStack key={index} spacing={cardSpacing}>
                                                <Textarea
                                                    value={note}
                                                    onChange={(e) => handleNoteChange(index, e.target.value)}
                                                    placeholder={t('settings.agent.new.note-placeholder')}
                                                    bg={bgTextarea}
                                                    _placeholder={{ color: placeholderColor }}
                                                    fontSize="sm"
                                                    required
                                                />
                                                <Tooltip label={t("system.actions.remove")}>
                                                    <IconButton
                                                        size="sm"
                                                        icon={<CloseIcon />}
                                                        colorScheme="red"
                                                        onClick={() => handleRemoveNote(index)}
                                                        aria-label={t("system.actions.remove")}
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        ))}
                                    </Stack>
                                    <FormErrorMessage>
                                        {t(inputError[ids.agentObservations])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Flex w="100%" justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(ROUTES.agentsPath)}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    isDisabled={isFormInvalid}
                                    onClick={handleSubmit}
                                    _hover={isFormInvalid ? {} : undefined}
                                >
                                    {t(id ? 'system.actions.edit' : 'system.actions.save')}
                                </Button>
                            </Flex>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
            <PersonModal
                isOpen={isOpenPerson}
                onClose={onClosePerson}
                isPersonValid={isPersonValid}
                selectedPerson={selectedPerson}
                onSave={handleSavePerson}
            />
            <ConfirmDialog
                ref={dialogRef}
                onConfirm={confirmRemoveNote}
                confirmButtonProps={{ colorScheme: "red" }}
            />
        </Flex >
    );
};

export default AgentNew;